import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getUserSubjectHistory } from '../utils/subjectHistorysAPI';
import { Medal, Trophy, Award, Star, TrendingUp, CheckCircle } from 'lucide-react';

// 成就定义
const achievementsList = [
  {
    id: 'first_exam',
    name: '初学者',
    description: '完成第一次考试',
    icon: Medal,
    color: 'bg-yellow-100 text-yellow-600',
    requirement: history => history.length >= 1
  },
  {
    id: 'high_score',
    name: '高分达人',
    description: '在任意考试中获得90分以上的成绩',
    icon: Trophy,
    color: 'bg-blue-100 text-blue-600',
    requirement: history => history.some(h => (h.score || 0) >= 90)
  },
  {
    id: 'persistent',
    name: '坚持不懈',
    description: '至少完成5次考试',
    icon: Award,
    color: 'bg-purple-100 text-purple-600',
    requirement: history => history.length >= 5
  },
  {
    id: 'perfect',
    name: '完美答卷',
    description: '在一次考试中全部回答正确',
    icon: Star,
    color: 'bg-red-100 text-red-600',
    requirement: history => history.some(h => h.total_questions > 0 && h.correct_count === h.total_questions)
  },
  {
    id: 'improved',
    name: '大幅进步',
    description: '相比上一次考试，分数提高至少20分',
    icon: TrendingUp,
    color: 'bg-green-100 text-green-600',
    requirement: history => {
      if (history.length < 2) return false;
      const sortedHistory = [...history].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      for (let i = 1; i < sortedHistory.length; i++) {
        const prevScore = sortedHistory[i-1].score || 0;
        const currScore = sortedHistory[i].score || 0;
        if (currScore - prevScore >= 20) return true;
      }
      return false;
    }
  }
];

/**
 * 成就系统组件
 * 显示用户解锁的成就和未解锁的成就
 * @param {Object} props 
 * @param {string} props.userId - 用户ID (可选)
 * @param {Array} props.examHistory - 考试历史记录 (可选)
 */
function Achievements({ userId, examHistory = [] }) {
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // 使用useRef保存上一次的userId，用于判断是否需要重新获取数据
  const prevUserIdRef = useRef(null);
  
  // 使用useCallback包装数据获取逻辑，避免重复创建函数
  const fetchExamHistory = useCallback(async (effectiveUserId) => {
    try {
      console.log('获取考试历史记录:', effectiveUserId);
      // 使用API函数获取历史记录而非直接Supabase查询
      const data = await getUserSubjectHistory(effectiveUserId);
      
      console.log(`获取到${data?.length || 0}条考试历史记录`);
      return data || [];
    } catch (err) {
      console.error('获取考试历史记录出错:', err);
      return [];
    }
  }, []);
  
  // 计算成就
  const calculateAchievements = useCallback((historyData) => {
    // 计算解锁的成就
    return achievementsList.filter(achievement => 
      achievement.requirement(historyData)
    );
  }, []);
  
  // 主要数据获取逻辑 - 仅在userId或examHistory变化时执行
  useEffect(() => {
    const fetchAchievements = async () => {
      // 避免在同一个userId上重复获取数据
      if (prevUserIdRef.current === userId && !examHistory.length) {
        return;
      }
      
      // 更新当前userId的引用
      prevUserIdRef.current = userId;
      
      try {
        setLoading(true);
        
        // 1. 如果直接传入了examHistory，则直接使用
        if (examHistory && examHistory.length > 0) {
          const unlocked = calculateAchievements(examHistory);
          setUnlockedAchievements(unlocked);
          setLoading(false);
          return;
        }
        
        // 2. 如果有userId，使用它获取历史记录
        if (userId) {
          const historyData = await fetchExamHistory(userId);
          const unlocked = calculateAchievements(historyData);
          setUnlockedAchievements(unlocked);
          setLoading(false);
          return;
        }
        
        // 3. 尝试从本地存储获取用户ID
        const userOpenid = localStorage.getItem('user_openid');
        if (userOpenid) {
          // 尝试使用API直接用openid作为用户ID查询
          const historyData = await fetchExamHistory(userOpenid);
          if (historyData.length > 0) {
            const unlocked = calculateAchievements(historyData);
            setUnlockedAchievements(unlocked);
            setLoading(false);
            return;
          }
        }
        
        // 如果所有尝试都失败，显示空成就列表
        setLoading(false);
      } catch (error) {
        console.error('获取成就出错:', error.message);
        setUnlockedAchievements([]);
        setLoading(false);
      }
    };
    
    fetchAchievements();
  }, [userId, examHistory, fetchExamHistory, calculateAchievements]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // 已解锁和未解锁的成就
  const lockedAchievements = achievementsList.filter(
    achievement => !unlockedAchievements.some(a => a.id === achievement.id)
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg text-gray-800">成就系统</h3>
        <span className="text-sm text-primary-dark">
          {unlockedAchievements.length}/{achievementsList.length} 已解锁
        </span>
      </div>
      
      <div className="space-y-3">
        {/* 显示已解锁的成就 */}
        {unlockedAchievements.map((achievement) => {
          const IconComponent = achievement.icon;
          
          return (
            <div 
              key={achievement.id}
              className={`p-3 rounded-lg border ${achievement.color} relative`}
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-3">
                  <IconComponent className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-medium">{achievement.name}</h4>
                  <p className="text-xs">{achievement.description}</p>
                </div>
                
                <div className="absolute right-3 top-3 text-green-500">
                  <CheckCircle className="w-4 h-4" />
                </div>
              </div>
            </div>
          );
        })}
        
        {/* 显示未解锁的成就 */}
        {showAll && lockedAchievements.map((achievement) => {
          const IconComponent = achievement.icon;
          
          return (
            <div 
              key={achievement.id}
              className="p-3 rounded-lg border bg-gray-100 text-gray-400 relative"
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                  <IconComponent className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-medium">{achievement.name}</h4>
                  <p className="text-xs">{achievement.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* 显示/隐藏按钮 */}
      {achievementsList.length > unlockedAchievements.length && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium"
        >
          {showAll ? "收起" : `查看未解锁成就 (${achievementsList.length - unlockedAchievements.length})`}
        </button>
      )}
    </div>
  );
}

export default Achievements; 